import { useBoundStore } from '../../store/store'
import classNames from '../../utils/classNames'
import BrandLoading from './brand-loading'

// Full-viewport loading screen shown while a casino game or sports book iframe
// boots. When the CMS provides an enabled, themed animated WebP for the vertical
// we paint that; otherwise we fall back to the generic BrandLoading splash
// (logo + spinner).

// CMS override keys in the settings slice, keyed by vertical then theme. The
// backend serves a themed animated WebP per vertical; we use it when it is
// enabled (status '1') and carries a URL, and fall back to BrandLoading when it
// is not.
const REMOTE_KEY = {
  casino: { light: 'loading_casino_light', dark: 'loading_casino_dark' },
  sports: { light: 'loading_sports_light', dark: 'loading_sports_dark' },
} as const

interface GameLoadingProps {
  variant: keyof typeof REMOTE_KEY
  // Cover the whole viewport instead of just the content region. Mirrors
  // BrandLoading's `fullScreen` so callers can keep passing `isFullScreen`.
  fullScreen?: boolean
}

const GameLoading = ({ variant, fullScreen = false }: GameLoadingProps) => {
  // Resolve the CMS override in a single self-contained selector so it reads
  // the current theme straight from the store rather than closing over a value
  // from a separate hook.
  const remote = useBoundStore(state => {
    const themeKey = state.common.theme === 'light' ? 'light' : 'dark'
    return state.settings[REMOTE_KEY[variant][themeKey]]
  })

  // Use the CMS-configured image only when it is enabled and has a URL,
  // otherwise fall back to the brand splash.
  if (remote.status !== '1' || !remote.value) {
    return <BrandLoading fullScreen={fullScreen} />
  }

  return (
    <div
      className={classNames(
        'fixed left-0 flex w-screen items-center justify-center bg-white dark:bg-primary-bg',
        fullScreen
          ? 'top-0 z-[600] h-screen'
          : 'top-[60px] z-10 h-[calc(100dvh-120px)] xl:h-[calc(100dvh-60px)]',
      )}>
      <img
        className="h-1/2 w-auto object-contain"
        // LCP-critical: this is the full-viewport splash shown while the game /
        // sports iframe boots, competing with the iframe's asset downloads.
        fetchPriority="high"
        src={remote.value}
        alt="Cargando"
      />
    </div>
  )
}

export default GameLoading
